import type { ProfileInput, SectionAnalysis, Language } from "@/lib/index";

// ─── Translations for Analysis Logic ──────────────────────────────────────────
const MESSAGES: Record<Language, any> = {
  it: {
    labels: {
      headline: "Headline",
      about: "About",
      skills: "Competenze",
      coherence: "Coerenza",
      engagement: "Engagement",
      pillars: "Pillar",
      services: "Servizi",
      experience: "Esperienza",
      words: "parole",
      seeExpansion: "(Vedi suggerimenti in Home per l'espansione completa)",
      strategyApplied: "Strategia di Coerenza Semantica Applicata",
      frameworkGenerated: "Framework Strategico di Engagement Generato",
      mapCreated: "Mappa Strategica dei Content Pillar Creata",
    },
    analysis: {
      servicesGood: "La sezione servizi è ben strutturata e densa semanticamente",
      servicesMedium: "I servizi sono elencati ma manca una bio persuasiva",
      servicesBad: "La sezione servizi è assente o poco dettagliata",
      servicesDesc: "360Brew analizza i Servizi come un'estensione delle tue Skills: una bio specifica e una lista di servizi coerenti aumentano la probabilità di apparire nelle ricerche dirette dei clienti.",
      experienceGood: "La sezione esperienza è dettagliata e orientata ai risultati",
      experienceMedium: "L'esperienza è presente ma mancano dati quantitativi (ROI)",
      experienceBad: "L'esperienza è troppo sintetica per l'algoritmo Blue XL",
      experienceDesc: "360Brew valuta l'esperienza non solo come storico lavorativo, ma come prova di expertise semantica. Titoli chiari, tipo di impiego e una descrizione ricca di risultati misurabili e skills integrate sono fondamentali per il ranking.",
      good: "ha una buona struttura semantica",
      medium: "ha potenziale ma manca di densità semantica",
      bad: "necessita di una riscrittura completa",
      headlineDesc: "360Brew valuta la headline come il principale segnale di identità professionale: deve contenere ruolo, expertise e valore unico in modo esplicito per permettere al modello di posizionarti correttamente nel feed dei tuoi target.",
      aboutGood: "comunica bene la tua identità professionale",
      aboutMedium: "ha contenuto ma manca di struttura narrativa ottimale per 360Brew",
      aboutBad: "è insufficiente per essere distribuita dal modello AI di LinkedIn",
      aboutDesc: "360Brew legge l'About come testo semantico puro: ogni parola contribuisce al tuo posizionamento nel feed. La coerenza tra le keyword dell'About e quelle della Headline è un fattore critico di ranking.",
      skillsGood: "La copertura semantica è buona",
      skillsMedium: "La copertura è parziale",
      skillsBad: "Le competenze sono insufficienti per un ranking ottimale",
      skillsDesc: "360Brew usa le skills come segnali semantici per categorizzare il tuo profilo: più sono specifiche, rilevanti e numerose (fino a 50), maggiore è la precisione del sistema nel distribuire i tuoi contenuti al pubblico giusto.",
      coherenceGood: "un buon allineamento tematico",
      coherenceMedium: "una coerenza parziale che può essere migliorata",
      coherenceBad: "una mancanza di allineamento che 360Brew penalizzerà",
      coherenceDesc: "Il modello AI di LinkedIn effettua un cross-check costante tra Headline, About, Skills ed Experience: la \"semantic coherence\" è diventata un fattore di ranking primario nel 2025.",
      engagementHigh: "è elevato",
      engagementMedium: "è discreto ma migliorabile",
      engagementLow: "è basso e richiede interventi strutturali",
      engagementDesc: "360Brew valuta l'engagement quality, non la quantity: commenti profondi, salvataggi e dwell time pesano molto di più dei semplici like. Il tuo profilo deve essere costruito per stimolare interazioni di valore, non reazioni superficiali.",
      pillarsGood: "è ben definita",
      pillarsMedium: "è parzialmente definita",
      pillarsBad: "non è ancora chiaramente strutturata",
      pillarsDesc: "360Brew premia la coerenza tematica nel tempo: un profilo che tratta 2-4 argomenti specifici con costanza viene distribuito a una community più ampia e qualificata rispetto a chi pubblica contenuti casuali o eterogenei.",
    },
    suggestions: {
      headline: [
        "Usa la Formula: [Ruolo] | [Competenza Chiave] | [Risultato Specifico] | [Tocco Personale]",
        "Metti le info più importanti nei primi 60-80 caratteri per la visibilità mobile",
        "SEO: Usa keyword specifiche (es. 'B2B Sales') invece di termini vaghi come 'Visionario'",
        "Focus sull'Output: Dì cosa fai per gli altri (es. 'Aiuto le aziende a ridurre i costi del 20%')",
        "Usa separatori visivi come la barra verticale ( | ) per la massima leggibilità",
        "Elimina i Buzzword: Evita termini come 'Appassionato', 'Motivato' o 'Hard-working'",
        "Espandi fino a 200-220 caratteri totali per massimizzare la densità semantica",
      ],
      about: [
        "Espandi a 1500-2000 caratteri (300-400 parole) per massimizzare il dwell time su 360Brew",
        "Struttura in 4 paragrafi: Chi sei → Cosa fai → Risultati → CTA",
        "Aggiungi una Call To Action finale con link o invito a connettersi",
        "Inizia con un hook forte nei primi 3 righe (visibili senza cliccare 'Vedi altro')",
        "Inserisci 3-5 numeri/dati concreti che dimostrano il tuo impatto misurabile",
        "Usa le stesse keyword della tua headline per creare coerenza semantica riconosciuta da 360Brew",
        "Aggiungi una lista di punti con • per aumentare la leggibilità e il tempo di lettura",
      ],
      skills: [
        "Aggiungi almeno 15-20 competenze per massimizzare la copertura semantica",
        "LinkedIn consente fino a 50 skills: usa l'intero spazio disponibile",
        "Organizza le skill in 3 categorie: Core Skills (3-5), Tools & Tech (5-8), Soft Skills (3-5)",
        "Le prime 3 skill sono le più visibili: metti qui le tue competenze distintive principali",
        "Allinea le skills alle keyword più ricercate nel tuo settore su LinkedIn",
        "Chiedi endorsement per le top 5 skills ai colleghi: aumentano il peso semantico per 360Brew",
        "Rimuovi skills generiche come 'Microsoft Office' — 360Brew premia la specificità",
      ],
      coherence: [
        "Usa le stesse 3-5 keyword chiave in Headline, About, Skills ed Experience",
        "Il tuo 'narrative thread' deve essere riconoscibile: ogni sezione deve raccontare la stessa storia",
        "Evita contraddizioni semantiche: non essere 'esperto di AI' nella headline e parlare solo di vendite nell'About",
        "Allinea il titolo del tuo ruolo attuale con il posizionamento dichiarato nella headline",
        "360Brew penalizza la 'semantic mismatch': profilo da marketer che posta di sport = calo reach",
        "Fai un 'semantic audit': elenca 5 keyword chiave e verifica che siano in TUTTE le sezioni",
        "Il Featured Section deve contenere contenuti coerenti con il tuo posizionamento dichiarato",
      ],
      engagement: [
        "Inserisci domande aperte nel tuo About: stimolano commenti di qualità (alto peso per 360Brew)",
        "Usa dati e numeri specifici: aumentano il dwell time e la credibilità semantica",
        "Il Featured Section è il tuo 'engagement hub': inserisci contenuti evergreen ad alto valore",
        "Pubblica con cadenza 3-5 volte a settimana: 360Brew penalizza sia la sovraesposizione che l'inattività",
        "I commenti approfonditi (>50 parole) pesano 5x i like: rispondi sempre con contenuto",
        "Crea contenuti 'save-worthy': liste, framework, checklist — 360Brew premia i salvataggi",
        "Evita l'engagement bait ('Commenta SÌ o NO') — 360Brew lo penalizza attivamente",
      ],
      pillars: [
        "Scegli 2-4 pillar tematici e presidiiali con costanza per almeno 90 giorni",
        "Ogni pillar deve avere una 'content cadence': 1-2 post a settimana per pillar",
        "I pillar devono essere semanticamente coerenti con la tua headline e il tuo About",
        "Mappa ogni pillar a un tipo di contenuto: educational, case study, opinione, dati",
        "360Brew distribuisce di più chi ha consistenza tematica su 30-60 giorni",
        "Crea un 'content calendar' con i 4 pillar e ruotali settimanalmente",
        "Il pillar principale deve essere identico al tuo posizionamento core nella headline",
      ],
      experience: [
        "Usa titoli lavorativi standardizzati per massimizzare il riconoscimento algoritmico",
        "Specifica sempre il tipo di impiego (Full-time, Freelance, ecc.)",
        "Usa il framework: Azione + Metrica + Risultato nelle descrizioni",
        "Integra 3-5 competenze chiave direttamente in ogni descrizione",
        "Mantieni la coerenza tra il ruolo attuale e la tua Headline",
        "Assicurati che il nome dell'azienda sia corretto per il linking automatico"
      ],
      services: [
        "Crea una bio specifica di 500 caratteri densa di keyword semantiche",
        "Elenca 5-10 servizi specifici separati da virgola",
        "Assicura l'allineamento tra i servizi e le tue top skills",
        "Usa termini rivolti al cliente (es. 'Consulenza Strategica' invece di 'Lavoro su...')",
        "360Brew premia la specificità: evita 'Consulenza generica'",
        "Allinea i servizi alla domanda attuale del mercato nel tuo settore"
      ]
    },
    optimized: {
      services: (input: any) => {
        const { servicesBio, servicesList } = input;
        const bio = servicesBio || "Expert consultation and strategic growth solutions.";
        const list = servicesList.split(",").map((s: string) => s.trim()).filter(Boolean).slice(0, 10).join(", ");
        return `✅ SERVICES BIO:\n${bio.substring(0, 500)}\n\n🛠 CORE SERVICES:\n${list}`;
      },
      headline: (input: any) => {
        const { position, industry, skills } = input;
        const role = position || "Expert";
        const sector = industry || "Professional";
        const skillArray = skills.split(",").map((s: string) => s.trim()).filter(Boolean);
        const coreSkills = skillArray.slice(0, 8).join(" | ");
        const text = `${role} | ${sector} | ${coreSkills}`;
        return text.substring(0, 200);
      },
      about: (input: any) => {
        const { position, industry, skills, fullName } = input;
        const name = fullName || "Professionista";
        const role = position || "Expert";
        const sector = industry || "Settore";
        const skillList = skills.split(",").map((s: string) => s.trim()).filter(Boolean).slice(0, 10);
        
        const aboutText = `🚀 360Brew Optimized Narrative for ${name}\n\nAs a ${role} in the ${sector} industry, I specialize in transforming complex challenges into strategic growth opportunities. My professional DNA is built on a foundation of data-driven decision making and a commitment to delivering measurable ROI.\n\n💡 MY CORE MISSION:\nTo bridge the gap between technical expertise and business outcomes, ensuring that every project scales what truly matters.\n\n🎯 KEY AREAS OF EXPERTISE:\n${skillList.map((s: string) => `• ${s}`).join("\n")}\n\n📈 IMPACT & RESULTS:\nThroughout my career, I have focused on building resilient systems that drive efficiency and innovation. By leveraging 360Brew semantic logic, I ensure that my professional narrative aligns perfectly with the evolving needs of the global market.\n\n🤝 Let's connect to discuss ${sector}, systems architecture, or the future of AI-driven growth. I am always open to high-value interactions and strategic partnerships.\n\n#${sector.replace(/\s+/g, "")} #GrowthSystems #ProfessionalStorytelling`;
        
        return aboutText.substring(0, 2000);
      }
    }
  },
  en: {
    labels: {
      headline: "Headline",
      about: "About",
      skills: "Skills",
      coherence: "Coherence",
      engagement: "Engagement",
      pillars: "Pillars",
      services: "Services",
      words: "words",
      seeExpansion: "(See suggested lists in Home for full expansion)",
      strategyApplied: "Semantic Coherence Strategy Applied",
      frameworkGenerated: "Engagement Strategy Framework Generated",
      mapCreated: "Content Pillars Strategy Map Created",
    },
    analysis: {
      servicesGood: "The services section is well-structured and semantically dense",
      servicesMedium: "Services are listed but a persuasive bio is missing",
      servicesBad: "The services section is absent or lacks detail",
      servicesDesc: "360Brew analyzes Services as an extension of your Skills: a specific bio and a list of consistent services increase the probability of appearing in direct client searches.",
      experienceGood: "The experience section is detailed and results-oriented",
      experienceMedium: "Experience is present but lacks quantitative data (ROI)",
      experienceBad: "Experience is too synthetic for the Blue XL algorithm",
      experienceDesc: "360Brew evaluates experience not just as job history, but as proof of semantic expertise. Clear titles, employment type, and a description rich in measurable results and integrated skills are fundamental for ranking.",
      good: "has a good semantic structure",
      medium: "has potential but lacks semantic density",
      bad: "needs a complete rewrite",
      headlineDesc: "360Brew evaluates the headline as the primary signal of professional identity: it must explicitly contain role, expertise, and unique value to allow the model to correctly position you in your targets' feed.",
      aboutGood: "communicates your professional identity well",
      aboutMedium: "has content but lacks optimal narrative structure for 360Brew",
      aboutBad: "is insufficient to be distributed by LinkedIn's AI model",
      aboutDesc: "360Brew reads the About section as pure semantic text: every word contributes to your positioning in the feed. Coherence between About keywords and Headline keywords is a critical ranking factor.",
      skillsGood: "Semantic coverage is good",
      skillsMedium: "Coverage is partial",
      skillsBad: "Skills are insufficient for optimal ranking",
      skillsDesc: "360Brew uses skills as semantic signals to categorize your profile: the more specific, relevant, and numerous they are (up to 50), the higher the system's precision in distributing your content to the right audience.",
      coherenceGood: "good thematic alignment",
      coherenceMedium: "partial coherence that can be improved",
      coherenceBad: "a lack of alignment that 360Brew will penalize",
      coherenceDesc: "LinkedIn's AI model constantly cross-checks Headline, About, Skills, and Experience: \"semantic coherence\" has become a primary ranking factor in 2025.",
      engagementHigh: "is high",
      engagementMedium: "is fair but improvable",
      engagementLow: "is low and requires structural interventions",
      engagementDesc: "360Brew evaluates engagement quality, not quantity: deep comments, saves, and dwell time weigh much more than simple likes. Your profile must be built to stimulate value interactions, not superficial reactions.",
      pillarsGood: "is well-defined",
      pillarsMedium: "is partially defined",
      pillarsBad: "is not yet clearly structured",
      pillarsDesc: "360Brew rewards thematic coherence over time: a profile that consistently covers 2-4 specific topics is distributed to a wider and more qualified community than those who publish random or heterogeneous content.",
    },
    suggestions: {
      headline: [
        "Use the Formula: [Job Title] | [Key Skill] | [Specific Result] | [Personal Hook]",
        "Keep the most important info in the first 60-80 characters for mobile visibility",
        "SEO Focus: Use specific industry keywords, not 'Creative Visionary'",
        "Output Focus: Say what you do for others (e.g., 'Helping SMBs reduce overhead by 20%')",
        "Use Visual Dividers: Use vertical pipes ( | ) for professional structure",
        "Ditch Buzzwords: Avoid filler like 'Passionate', 'Motivated', or 'Hard-working'",
        "Expand up to 200-220 characters to maximize semantic density",
      ],
      about: [
        "Expand to 1500-2000 characters (300-400 words) to maximize dwell time on 360Brew",
        "Structure in 4 paragraphs: Who you are → What you do → Results → CTA",
        "Add a final Call To Action with a link or invitation to connect",
        "Start with a strong hook in the first 3 lines (visible without clicking 'See more')",
        "Include 3-5 concrete numbers/data points that demonstrate your measurable impact",
        "Use the same keywords as your headline to create semantic coherence recognized by 360Brew",
        "Add a bulleted list with • to increase readability and reading time",
      ],
      skills: [
        "Add at least 15-20 skills to maximize semantic coverage",
        "LinkedIn allows up to 50 skills: use the entire available space",
        "Organize skills into 3 categories: Core Skills (3-5), Tools & Tech (5-8), Soft Skills (3-5)",
        "The first 3 skills are the most visible: put your main distinctive skills here",
        "Align skills with the most searched keywords in your sector on LinkedIn",
        "Ask for endorsements for top 5 skills from colleagues: they increase semantic weight for 360Brew",
        "Remove generic skills like 'Microsoft Office' — 360Brew rewards specificity",
      ],
      coherence: [
        "Use the same 3-5 key keywords in Headline, About, Skills, and Experience",
        "Your 'narrative thread' must be recognizable: every section must tell the same story",
        "Avoid semantic contradictions: don't be an 'AI expert' in the headline and only talk about sales in the About section",
        "Align your current job title with the positioning declared in the headline",
        "360Brew penalizes 'semantic mismatch': marketer profile posting about sports = drop in reach",
        "Perform a 'semantic audit': list 5 key keywords and verify they are in ALL sections",
        "The Featured Section must contain content consistent with your declared positioning",
      ],
      engagement: [
        "Insert open questions in your About section: they stimulate quality comments (high weight for 360Brew)",
        "Use specific data and numbers: they increase dwell time and semantic credibility",
        "The Featured Section is your 'engagement hub': insert high-value evergreen content",
        "Post 3-5 times a week: 360Brew penalizes both overexposure and inactivity",
        "In-depth comments (>50 words) weigh 5x more than likes: always respond with content",
        "Create 'save-worthy' content: lists, frameworks, checklists — 360Brew rewards saves",
        "Avoid engagement bait ('Comment YES or NO') — 360Brew actively penalizes it",
      ],
      pillars: [
        "Choose 2-4 thematic pillars and maintain them consistently for at least 90 days",
        "Each pillar should have a 'content cadence': 1-2 posts per week per pillar",
        "Pillars must be semantically consistent with your headline and About section",
        "Map each pillar to a content type: educational, case study, opinion, data",
        "360Brew distributes more to those with thematic consistency over 30-60 days",
        "Create a 'content calendar' with the 4 pillars and rotate them weekly",
        "The main pillar must be identical to your core positioning in the headline",
      ],
      experience: [
        "Use standardized job titles to maximize algorithmic recognition",
        "Always include employment type (Full-time, Freelance, etc.)",
        "In the description, use the framework: Action + Metric + Result",
        "Include at least 3-5 specific skills used in that role",
        "Avoid passive descriptions; use strong action verbs like 'Led', 'Developed', 'Optimized'",
        "Maintain consistency between the current role and your Headline",
        "Ensure the company name is correct for automatic linking"
      ],
      services: [
        "Create a specific 500-character bio dense with semantic keywords",
        "List 5-10 specific services separated by commas",
        "Ensure alignment between services and your top skills",
        "Use client-facing terms (e.g., 'Strategic Consulting' instead of 'Working on...')",
        "360Brew rewards specificity: avoid 'Generic Consulting'",
        "Align services with current market demand in your sector"
      ]
    },
    optimized: {
      services: (input: any) => {
        const { servicesBio, servicesList } = input;
        const bio = servicesBio || "Expert consultation and strategic growth solutions.";
        const list = servicesList.split(",").map((s: string) => s.trim()).filter(Boolean).slice(0, 10).join(", ");
        return `✅ SERVICES BIO:\n${bio.substring(0, 500)}\n\n🛠 CORE SERVICES:\n${list}`;
      },
      headline: (input: any) => {
        const { position, industry, skills } = input;
        const role = position || "Professional";
        const sector = industry || "Industry";
        const skillArray = skills.split(",").map((s: string) => s.trim()).filter(Boolean);
        const coreSkills = skillArray.slice(0, 8).join(" | ");
        const text = `${role} | ${sector} | ${coreSkills}`;
        return text.substring(0, 200);
      },
      about: (input: any) => {
        const { position, industry, skills, fullName } = input;
        const name = fullName || "Professional";
        const role = position || "Expert";
        const sector = industry || "Sector";
        const skillList = skills.split(",").map((s: string) => s.trim()).filter(Boolean).slice(0, 10);
        
        const aboutText = `🚀 360Brew Optimized Narrative for ${name}\n\nAs a ${role} in the ${sector} industry, I specialize in transforming complex challenges into strategic growth opportunities. My professional DNA is built on a foundation of data-driven decision making and a commitment to delivering measurable ROI.\n\n💡 MY CORE MISSION:\nTo bridge the gap between technical expertise and business outcomes, ensuring that every project scales what truly matters.\n\n🎯 KEY AREAS OF EXPERTISE:\n${skillList.map((s: string) => `• ${s}`).join("\n")}\n\n📈 IMPACT & RESULTS:\nThroughout my career, I have focused on building resilient systems that drive efficiency and innovation. By leveraging 360Brew semantic logic, I ensure that my professional narrative aligns perfectly with the evolving needs of the global market.\n\n🤝 Let's connect to discuss ${sector}, systems architecture, or the future of AI-driven growth. I am always open to high-value interactions and strategic partnerships.\n\n#${sector.replace(/\s+/g, "")} #GrowthSystems #ProfessionalStorytelling`;
        
        return aboutText.substring(0, 2000);
      },
      experience: (input: any) => {
        const { experienceTitle, experienceType, experienceCompany, experienceDescription, experienceSkills } = input;
        const title = experienceTitle || "Role";
        const company = experienceCompany || "Company";
        const type = experienceType || "Full-time";
        const desc = experienceDescription || "Strategic leadership and operational excellence.";
        const skills = experienceSkills || "Leadership, Strategy";
        return `🏢 EXPERIENCE OPTIMIZED:\n${title} | ${company} (${type})\n\n📝 DESCRIPTION:\n${desc.substring(0, 1500)}\n\n🛠 SKILLS UTILIZED:\n${skills}`;
      }
    }
  },
  de: {
    labels: {
      headline: "Headline",
      about: "Info",
      skills: "Kenntnisse",
      coherence: "Kohärenz",
      engagement: "Engagement",
      pillars: "Säulen",
      services: "Dienstleistungen",
      experience: "Erfahrung",
      words: "Wörter",
      seeExpansion: "(Siehe Vorschlagslisten in Home für vollständige Erweiterung)",
      strategyApplied: "Semantische Kohärenzstrategie angewendet",
      frameworkGenerated: "Engagement-Strategie-Framework generiert",
      mapCreated: "Content-Säulen-Strategiekarte erstellt",
    },
    analysis: {
      servicesGood: "Der Bereich Dienstleistungen ist gut strukturiert und semantisch dicht",
      servicesMedium: "Dienstleistungen sind aufgeführt, aber eine überzeugende Bio fehlt",
      servicesBad: "Der Bereich Dienstleistungen fehlt oder ist nicht detailliert genug",
      servicesDesc: "360Brew analysiert Dienstleistungen als Erweiterung Ihrer Kenntnisse: Eine spezifische Bio und eine Liste konsistenter Dienstleistungen erhöhen die Wahrscheinlichkeit, in direkten Kundensuchanfragen zu erscheinen.",
      good: "hat eine gute semantische Struktur",
      medium: "hat Potenzial, aber es fehlt an semantischer Dichte",
      bad: "erfordert eine komplette Neugestaltung",
      headlineDesc: "360Brew bewertet die Headline als das primäre Signal der beruflichen Identität: Sie muss explizit Rolle, Fachwissen und einzigartigen Wert enthalten, damit das Modell Sie korrekt im Feed Ihrer Zielgruppen positionieren kann.",
      aboutGood: "kommuniziert Ihre berufliche Identität gut",
      aboutMedium: "hat Inhalt, aber es fehlt die optimale narrative Struktur für 360Brew",
      aboutBad: "reicht nicht aus, um vom KI-Modell von LinkedIn verteilt zu werden",
      aboutDesc: "360Brew liest den Info-Bereich als rein semantischen Text: Jedes Wort trägt zu Ihrer Positionierung im Feed bei. Die Kohärenz zwischen Info-Keywords und Headline-Keywords ist ein kritischer Ranking-Faktor.",
      skillsGood: "Semantische Abdeckung ist gut",
      skillsMedium: "Abdeckung ist teilweise",
      skillsBad: "Kenntnisse reichen für ein optimales Ranking nicht aus",
      skillsDesc: "360Brew nutzt Kenntnisse als semantische Signale, um Ihr Profil zu kategorisieren: Je spezifischer, relevanter und zahlreicher sie sind (bis zu 50), desto höher ist die Präzision des Systems bei der Verteilung Ihrer Inhalte an das richtige Publikum.",
      coherenceGood: "gute thematische Ausrichtung",
      coherenceMedium: "teilweise Kohärenz, die verbessert werden kann",
      coherenceBad: "mangelnde Ausrichtung, die 360Brew bestrafen wird",
      coherenceDesc: "Das KI-Modell von LinkedIn führt einen ständigen Abgleich zwischen Headline, Info, Kenntnissen und Erfahrung durch: \"Semantische Kohärenz\" ist im Jahr 2025 zu einem primären Ranking-Faktor geworden.",
      engagementHigh: "ist hoch",
      engagementMedium: "ist ordentlich, aber verbesserungswürdig",
      engagementLow: "ist niedrig und erfordert strukturelle Eingriffe",
      engagementDesc: "360Brew bewertet die Engagement-Qualität, nicht die Quantität: Tiefgründige Kommentare, Speichern und Verweildauer wiegen viel schwerer als einfache Likes. Ihr Profil muss so aufgebaut sein, dass es wertvolle Interaktionen anregt, keine oberflächlichen Reaktionen.",
      pillarsGood: "ist gut definiert",
      pillarsMedium: "ist teilweise definiert",
      pillarsBad: "ist noch nicht klar strukturiert",
      pillarsDesc: "360Brew belohnt thematische Kohärenz im Laufe der Zeit: Ein Profil, das konsequent 2-4 spezifische Themen abdeckt, wird an eine breitere und qualifiziertere Community verteilt als Profile, die zufällige oder heterogene Inhalte veröffentlichen.",
      experienceGood: "Der Erfahrungsbereich ist detailliert und ergebnisorientiert",
      experienceMedium: "Erfahrung ist vorhanden, aber es fehlen quantitative Daten (ROI)",
      experienceBad: "Die Erfahrung ist zu synthetisch für lo den Blue XL-Algorithmus",
      experienceDesc: "360Brew bewertet Erfahrung nicht nur als Jobhistorie, sondern als Beweis für semantische Expertise. Klare Titel, Beschäftigungsart und eine Beschreibung voller messbarer Ergebnisse und integrierter Fähigkeiten sind grundlegend für das Ranking.",
    },
    suggestions: {
      headline: [
        "Nutzen Sie die Formel: [Jobtitel] | [Kernkompetenz] | [Spezifisches Ergebnis] | [Hook]",
        "Wichtige Infos in die ersten 60-80 Zeichen setzen für mobile Sichtbarkeit",
        "SEO-Fokus: Spezifische Branchen-Keywords nutzen, nicht 'Kreativer Visionär'",
        "Output-Fokus: Sagen Sie, was Sie für andere tun (z.B. 'Hilft KMU bei der Kostenersparnis')",
        "Visuelle Trenner: Nutzen Sie vertikale Striche ( | ) für professionelle Struktur",
        "Keine Buzzwords: Vermeiden Sie Füllwörter wie 'Leidenschaftlich' oder 'Motiviert'",
        "Erweitern Sie auf 200-220 Zeichen, um die semantische Dichte zu maximieren",
      ],
      about: [
        "Auf 1500-2000 Zeichen (300-400 Wörter) erweitern, um die Verweildauer auf 360Brew zu maximieren",
        "In 4 Absätzen strukturieren: Wer Sie sind → Was Sie tun → Ergebnisse → CTA",
        "Fügen Sie einen abschließenden Call-to-Action mit einem Link oder einer Einladung zum Vernetzen hinzu",
        "Beginnen Sie mit einem starken Hook in den ersten 3 Zeilen (sichtbar ohne auf 'Mehr anzeigen' zu klicken)",
        "Fügen Sie 3-5 konkrete Zahlen/Datenpunkte ein, die Ihren messbaren Erfolg belegen",
        "Verwenden Sie dieselben Keywords wie in Ihrer Headline, um eine von 360Brew erkannte semantische Kohärenz zu schaffen",
        "Fügen Sie eine Aufzählung mit • hinzu, um die Lesbarkeit und Lesezeit zu erhöhen",
      ],
      skills: [
        "Fügen Sie mindestens 15-20 Kenntnisse hinzu, um die semantische Abdeckung zu maximieren",
        "LinkedIn erlaubt bis zu 50 Kenntnisse: Nutzen Sie den gesamten verfügbaren Platz",
        "Organisieren Sie Kenntnisse in 3 Kategorien: Kernkompetenzen (3-5), Tools & Tech (5-8), Soft Skills (3-5)",
        "Die ersten 3 Kenntnisse sind am sichtbarsten: Platzieren Sie hier Ihre wichtigsten Unterscheidungsmerkmale",
        "Richten Sie Kenntnisse an den meistgesuchten Keywords Ihrer Branche auf LinkedIn aus",
        "Bitten Sie Kollegen um Bestätigungen für die Top 5 Kenntnisse: Sie erhöhen das semantische Gewicht für 360Brew",
        "Entfernen Sie generische Kenntnisse wie 'Microsoft Office' — 360Brew belohnt Spezifität",
      ],
      coherence: [
        "Verwenden Sie dieselben 3-5 Schlüsselbegriffe in Headline, Info, Kenntnissen und Erfahrung",
        "Ihr 'roter Faden' muss erkennbar sein: Jeder Abschnitt muss dieselbe Geschichte erzählen",
        "Vermeiden Sie semantische Widersprüche: Seien Sie kein 'KI-Experte' in der Headline und sprechen Sie im Info-Bereich nur über Vertrieb",
        "Richten Sie Ihren aktuellen Jobtitel an der in der Headline deklarierten Positionierung aus",
        "360Brew bestraft 'semantische Nichtübereinstimmung': Marketer-Profil, das über Sport postet = Reichweitenverlust",
        "Führen Sie ein 'semantisches Audit' durch: Listen Sie 5 Schlüsselbegriffe auf und prüfen Sie, ob sie in ALLEN Abschnitten enthalten sind",
        "Der Featured-Bereich muss Inhalte enthalten, die mit Ihrer deklarierten Positionierung übereinstimmen",
      ],
      engagement: [
        "Stellen Sie offene Fragen in Ihrem Info-Bereich: Sie regen zu qualitativ hochwertigen Kommentaren an (hohes Gewicht für 360Brew)",
        "Verwenden Sie spezifische Daten und Zahlen: Sie erhöhen die Verweildauer und die semantische Glaubwürdigkeit",
        "Der Featured-Bereich ist Ihr 'Engagement-Hub': Fügen Sie hochwertige Evergreen-Inhalte ein",
        "Posten Sie 3-5 Mal pro Woche: 360Brew bestraft sowohl Überexposition als auch Inaktivität",
        "Ausführliche Kommentare (>50 Wörter) wiegen 5x mehr als Likes: Antworten Sie immer mit Inhalt",
        "Erstellen Sie 'speichernswerte' Inhalte: Listen, Frameworks, Checklisten — 360Brew belohnt Speichern",
        "Vermeiden Sie Engagement-Bait ('Kommentieren Sie JA oder NEIN') — 360Brew bestraft dies aktiv",
      ],
      pillars: [
        "Wählen Sie 2-4 thematische Säulen und pflegen Sie diese konsequent für mindestens 90 Tage",
        "Jede Säule sollte eine 'Content-Kadenz' haben: 1-2 Posts pro Woche pro Säule",
        "Säulen müssen semantisch konsistent mit Ihrer Headline und Ihrem Info-Bereich sein",
        "Ordnen Sie jede Säule einem Inhaltstyp zu: Bildung, Fallstudie, Meinung, Daten",
        "360Brew verteilt mehr an diejenigen mit thematischer Konsistenz über 30-60 Tage",
        "Erstellen Sie einen 'Content-Kalender' mit den 4 Säulen und rotieren Sie diese wöchentlich",
        "Die Hauptsäule muss identisch mit Ihrer Kernpositionierung in der Headline sein",
      ],
      experience: [
        "Verwenden Sie standardisierte Jobtitel, um die algorithmische Erkennung zu maximieren",
        "Geben Sie immer die Beschäftigungsart an (Vollzeit, Freiberuflich usw.)",
        "Verwenden Sie in der Beschreibung den Rahmen: Aktion + Metrik + Ergebnis",
        "Geben Sie mindestens 3-5 spezifische Fähigkeiten an, die in dieser Rolle verwendet wurden",
        "Vermeiden Sie passive Beschreibungen; verwenden Sie starke Aktionsverben wie 'Geführt', 'Entwickelt', 'Optimiert'",
        "Wahren Sie die Konsistenz zwischen der aktuellen Rolle und Ihrer Headline",
        "Stellen Sie sicher, dass der Firmenname für die automatische Verlinkung korrekt ist"
      ],
      services: [
        "Erstellen Sie eine spezifische 500-Zeichen-Bio voller semantischer Keywords",
        "Listen Sie 5-10 spezifische Dienstleistungen durch Kommas getrennt auf",
        "Stellen Sie die Ausrichtung zwischen Dienstleistungen und Ihren Top-Kenntnissen sicher",
        "Verwenden Sie kundenorientierte Begriffe (z.B. 'Strategische Beratung' statt 'Arbeit an...')",
        "360Brew belohnt Spezifität: Vermeiden Sie 'Allgemeine Beratung'",
        "Richten Sie Dienstleistungen an der aktuellen Marktnachfrage in Ihrem Sektor aus"
      ]
    },
    optimized: {
      services: (input: any) => {
        const { servicesBio, servicesList } = input;
        const bio = servicesBio || "Expertenberatung und strategische Wachstumslösungen.";
        const list = servicesList.split(",").map((s: string) => s.trim()).filter(Boolean).slice(0, 10).join(", ");
        return `✅ DIENSTLEISTUNGS-BIO:\n${bio.substring(0, 500)}\n\n🛠 KERNLEISTUNGEN:\n${list}`;
      },
      headline: (input: any) => {
        const { position, industry, skills } = input;
        const role = position || "Experte";
        const sector = industry || "Fachbereich";
        const skillArray = skills.split(",").map((s: string) => s.trim()).filter(Boolean);
        const coreSkills = skillArray.slice(0, 8).join(" | ");
        const text = `${role} | ${sector} | ${coreSkills}`;
        return text.substring(0, 200);
      },
      about: (input: any) => {
        const { position, industry, skills, fullName } = input;
        const name = fullName || "Experte";
        const role = position || "Experte";
        const sector = industry || "Fachbereich";
        const skillList = skills.split(",").map((s: string) => s.trim()).filter(Boolean).slice(0, 10);
        
        const aboutText = `🚀 360Brew Optimierte Erzählung für ${name}\n\nAls ${role} in der ${sector}-Branche bin ich darauf spezialisiert, komplexe Herausforderungen in strategische Wachstumschancen zu verwandeln. Meine berufliche DNA basiert auf einer Grundlage datengesteuerter Entscheidungsfindung und der Verpflichtung, messbaren ROI zu liefern.\n\n💡 MEINE KERNAUFGABE:\nDie Lücke zwischen technischem Fachwissen und Geschäftsergebnissen zu schließen und sicherzustellen, dass jedes Projekt das skaliert, was wirklich zählt.\n\n🎯 SCHLÜSSELKOMPETENZEN:\n${skillList.map((s: string) => `• ${s}`).join("\n")}\n\n📈 WIRKUNG & ERGEBNISSE:\nIm Laufe meiner Karriere habe ich mich auf den Aufbau belastbarer Systeme konzentriert, die Effizienz und Innovation fördern. Durch die Nutzung der 360Brew-Semantiklogik stelle ich sicher, dass meine berufliche Erzählung perfekt auf die sich entwickelnden Anforderungen des globalen Marktes abgestimmt ist.\n\n🤝 Lassen Sie uns vernetzen, um über ${sector}, Systemarchitektur oder die Zukunft des KI-gesteuerten Wachstums zu sprechen. Ich bin immer offen für hochwertige Interaktionen und strategische Partnerschaften.\n\n#${sector.replace(/\s+/g, "")} #Wachstumssysteme #BeruflichesStorytelling`;
        
        return aboutText.substring(0, 2000);
      },
      experience: (input: any) => {
        const { experienceTitle, experienceType, experienceCompany, experienceDescription, experienceSkills } = input;
        const title = experienceTitle || "Rolle";
        const company = experienceCompany || "Unternehmen";
        const type = experienceType || "Vollzeit";
        const desc = experienceDescription || "Strategische Führung und operative Exzellenz.";
        const skills = experienceSkills || "Führung, Strategie";
        return `🏢 ERFAHRUNG OPTIMIERT:\n${title} | ${company} (${type})\n\n📝 BESCHREIBUNG:\n${desc.substring(0, 1500)}\n\n🛠 GENUTZTE SKILLS:\n${skills}`;
      }
    }
  }
};

// ─── Scoring Logic ───────────────────────────────────────────────────────────
function scoreText(text: string, keywords: string[]): number {
  if (!text || text.trim().length < 10) return 15;
  let score = 40;
  const lower = text.toLowerCase();
  keywords.forEach((kw) => {
    if (kw && lower.includes(kw.toLowerCase())) score += 5;
  });
  if (text.length > 100) score += 10;
  if (text.length > 180) score += 10;
  if (text.includes("|") || text.includes("•") || text.includes("→")) score += 5;
  const hasNumbers = /\d+/.test(text);
  if (hasNumbers) score += 5;
  return Math.min(score, 85);
}

// ─── Analysis Functions ──────────────────────────────────────────────────────

export function analyzeHeadline(input: ProfileInput, lang: Language): SectionAnalysis {
  const { headline, position, industry } = input;
  let score = scoreText(headline, ["specialist", "expert", "manager", "director", "founder", "consultant", "strategist", "lead", "head", "senior", position, industry]);
  
  // Penalize buzzwords
  const buzzwords = ["passionate", "motivated", "hard-working", "creative", "visionary", "appassionato", "motivato", "creativo", "visionario", "leidenschaftlich", "motiviert", "kreativ"];
  const lowerHeadline = headline.toLowerCase();
  let penalty = 0;
  buzzwords.forEach(bw => {
    if (lowerHeadline.includes(bw)) penalty += 10;
  });
  score = Math.max(10, score - penalty);

  // 360Brew Blue XL: Cross-check with latest Experience Title
  if (input.experiences && input.experiences.length > 0) {
    const latestExp = input.experiences[0];
    const expWords = latestExp.title.toLowerCase().split(/\W+/).filter(w => w.length > 3);
    let expBonus = 0;
    expWords.forEach(word => {
      if (lowerHeadline.includes(word)) expBonus += 5;
    });
    score += Math.min(expBonus, 15);
  }

  // Bonus for dividers (|)
  if (headline.includes("|")) score += 5;

  score = Math.min(score, 98); // Max for headline before optimization

  const m = MESSAGES[lang].analysis;
  const l = MESSAGES[lang].labels;
  const s = MESSAGES[lang].suggestions.headline;
  const analysisMsg = `${l.headline} ${headline.length > 0 ? `("${headline.substring(0, 40)}...")` : "(...)"} ${score >= 70 ? m.good : score >= 50 ? m.medium : m.bad}. ${m.headlineDesc}`;
  return {
    score,
    analysis: analysisMsg,
    suggestions: s,
    optimizedText: MESSAGES[lang].optimized.headline(input),
  };
}

export function analyzeAbout(input: ProfileInput, lang: Language): SectionAnalysis {
  const { about, industry } = input;
  const score = scoreText(about, ["risultati", "results", "clienti", "growth", "impact", "value", "expertise", "experience", "strategy", "methodology", industry]);
  const wordCount = about.split(/\s+/).filter(Boolean).length;
  const m = MESSAGES[lang].analysis;
  const l = MESSAGES[lang].labels;
  const s = MESSAGES[lang].suggestions.about;
  const analysisMsg = `${l.about} ${wordCount > 0 ? `(${wordCount} ${l.words})` : "(...)"} ${score >= 70 ? m.aboutGood : score >= 50 ? m.aboutMedium : m.aboutBad}. ${m.aboutDesc}`;
  return {
    score,
    analysis: analysisMsg,
    suggestions: s,
    optimizedText: MESSAGES[lang].optimized.about(input),
  };
}

export function analyzeSkills(input: ProfileInput, lang: Language): SectionAnalysis {
  const { skills, position } = input;
  const skillList = skills.split(",").map((s) => s.trim()).filter(Boolean);
  const count = skillList.length;
  let score = 20 + (count >= 5 ? 20 : 0) + (count >= 10 ? 15 : 0) + (count >= 15 ? 10 : 0) + (count >= 8 ? 10 : 0);
  if (position && skills.toLowerCase().includes(position.toLowerCase().split(" ")[0])) score += 10;
  score = Math.min(score, 85);
  const m = MESSAGES[lang].analysis;
  const l = MESSAGES[lang].labels;
  const s = MESSAGES[lang].suggestions.skills;
  const analysisMsg = `${l.skills} (${count}) ${score >= 70 ? m.skillsGood : score >= 50 ? m.skillsMedium : m.skillsBad}. ${m.skillsDesc}`;
  return {
    score,
    analysis: analysisMsg,
    suggestions: s,
    optimizedText: `• ${skillList.join("\n• ")}\n\n${l.seeExpansion}`,
  };
}

export function analyzeCoherence(input: ProfileInput, lang: Language): SectionAnalysis {
  const { headline, about, skills } = input;
  const allText = `${headline} ${about} ${skills}`.toLowerCase();
  const words = allText.split(/\W+/).filter((w) => w.length > 4);
  const freq: Record<string, number> = {};
  words.forEach((w) => { freq[w] = (freq[w] || 0) + 1; });
  const repeated = Object.values(freq).filter((v) => v >= 2).length;
  let score = Math.min(30 + (repeated >= 3 ? 15 : 0) + (repeated >= 6 ? 15 : 0) + (repeated >= 10 ? 15 : 0) + (headline.length > 0 && about.length > 0 ? 15 : 0), 85);
  const m = MESSAGES[lang].analysis;
  const l = MESSAGES[lang].labels;
  const s = MESSAGES[lang].suggestions.coherence;
  return {
    score,
    analysis: `${l.coherence} ${score >= 70 ? m.coherenceGood : score >= 50 ? m.coherenceMedium : m.coherenceBad}. ${m.coherenceDesc}`,
    suggestions: s,
    optimizedText: l.strategyApplied,
  };
}

export function analyzeEngagement(input: ProfileInput, lang: Language): SectionAnalysis {
  const { headline, about } = input;
  const hasSpecificity = /\d+/.test(headline + about);
  const length = (headline + about).length;
  let score = Math.min(30 + (hasSpecificity ? 20 : 0) + (length > 500 ? 15 : 0) + (length > 1000 ? 15 : 0), 80);
  const m = MESSAGES[lang].analysis;
  const l = MESSAGES[lang].labels;
  const s = MESSAGES[lang].suggestions.engagement;
  return {
    score,
    analysis: `${l.engagement} ${score >= 70 ? m.engagementHigh : score >= 50 ? m.engagementMedium : m.engagementLow}. ${m.engagementDesc}`,
    suggestions: s,
    optimizedText: l.frameworkGenerated,
  };
}

export function analyzePillars(input: ProfileInput, lang: Language): SectionAnalysis {
  const { headline, about, skills } = input;
  const allText = `${headline} ${about} ${skills}`.length;
  let score = Math.min(35 + (allText > 500 ? 15 : 0) + (skills.split(",").length >= 5 ? 30 : 0), 80);
  const m = MESSAGES[lang].analysis;
  const l = MESSAGES[lang].labels;
  const s = MESSAGES[lang].suggestions.pillars;
  return {
    score,
    analysis: `${l.pillars} ${score >= 70 ? m.pillarsGood : score >= 50 ? m.pillarsMedium : m.pillarsBad}. ${m.pillarsDesc}`,
    suggestions: s,
    optimizedText: l.mapCreated,
  };
}
      
export function analyzeServices(input: ProfileInput, lang: Language): SectionAnalysis {
  const { servicesBio, servicesList, industry } = input;
  const count = servicesList.split(",").filter(Boolean).length;
  let score = 20 + (count >= 3 ? 20 : 0) + (count >= 6 ? 15 : 0) + (servicesBio.length > 100 ? 15 : 0) + (servicesBio.length > 300 ? 15 : 0);
  if (industry && servicesBio.toLowerCase().includes(industry.toLowerCase())) score += 10;
  score = Math.min(score, 85);
  
  const m = MESSAGES[lang].analysis;
  const l = MESSAGES[lang].labels;
  const analysisMsg = `${l.services} ${score >= 70 ? m.servicesGood : score >= 50 ? m.servicesMedium : m.servicesBad}. ${m.servicesDesc}`;
  
  return {
    score,
    analysis: analysisMsg,
    suggestions: [
      "Keep the Services Bio under 500 characters for maximum impact",
      "Use semantic keywords that match your Headline and Skills",
      "List 5-10 specific services separated by commas",
      "Ensure the bio highlights the specific ROI you deliver",
      "Align services with current market demand in your sector"
    ],
    optimizedText: MESSAGES[lang].optimized.services(input),
  };
}

export function analyzeExperience(input: ProfileInput, lang: Language): SectionAnalysis {
  const { experiences, headline } = input;
  const m = MESSAGES[lang].analysis;
  const l = MESSAGES[lang].labels;
  const s = MESSAGES[lang].suggestions.experience;

  if (!experiences || experiences.length === 0) {
    return {
      score: 15,
      analysis: `${l.experience}: ${m.experienceBad}. ${m.experienceDesc}`,
      suggestions: s,
      optimizedText: "Add your professional experiences to start the Blue XL optimization.",
    };
  }

  let totalScore = 0;
  const optimizedBlocks: string[] = [];
  const lowerHeadline = headline.toLowerCase();

  experiences.forEach((exp) => {
    let expScore = scoreText(exp.description, [
      "risultati", "results", "roi", "growth", "impact", "managed", "led", "developed",
      exp.title, exp.company
    ]);

    // Blue XL Coherence Check with Headline
    const titleWords = exp.title.toLowerCase().split(/\W+/).filter(w => w.length > 3);
    let coherenceBonus = 0;
    titleWords.forEach(word => {
      if (lowerHeadline.includes(word)) coherenceBonus += 5;
    });
    
    const skillCount = exp.skills.split(",").filter(Boolean).length;
    expScore += Math.min(coherenceBonus, 15) + (skillCount >= 3 ? 10 : 0) + 5;
    expScore = Math.min(expScore, 98);
    totalScore += expScore;

    // Optimized Text per experience
    const optimized = `🏢 ${exp.title} | ${exp.company} (${exp.type})\n📝 ${exp.description.substring(0, 1000)}\n🛠 SKILLS: ${exp.skills}`;
    optimizedBlocks.push(optimized);
  });

  const finalScore = Math.round(totalScore / experiences.length);
  const analysisMsg = `${l.experience} (${experiences.length}) ${finalScore >= 75 ? m.experienceGood : finalScore >= 50 ? m.experienceMedium : m.experienceBad}. 360Brew Blue XL verified semantic alignment with your Headline.`;

  return {
    score: finalScore,
    analysis: analysisMsg,
    suggestions: s,
    optimizedText: optimizedBlocks.join("\n\n---\n\n"),
  };
}
